// The search-terms panel — what people actually typed to trigger our ads.
//
// Present in the MASTER (account-wide) view, not only after a drill, because it
// is one of the most useful things on the page; an "anchor jump" button up top
// reaches it so it isn't buried under the campaign table. On drill it FOCUS-LOCKS
// to the current entity.
//
// ⚠️ GOOGLE REPORTS SEARCH TERMS AT AD-GROUP GRAIN AND ABOVE. Focusing an
// individual AD leaves this panel at that ad's AD GROUP, and it says so — showing
// an empty panel, or silently pretending the terms belong to the one ad, would
// both be worse than stating the limit.

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { formatReportingAmount } from '../../lib/reportingMoney';
import { convertRowAmount } from './googleCurrency';
import type { GoogleMoneyDisplay } from './googleTrendSeries';
import { ColumnsMenu } from './ColumnsMenu';
import { NOT_MEASURED, formatCount, formatDecimal, formatPct } from './metricFormat';
import { LEVEL_LABEL } from './focusNav';
import { SEARCH_TERM_COLUMNS, type SearchTermColumnKey } from './tableWidgets';
import type {
  GoogleAdsSearchTermRow,
  GoogleAdsSearchTermsResponse,
} from '../../types/googleAdsReporting';

type SortKey = 'cost' | 'clicks' | 'impressions' | 'conversions' | 'ctr';

export const SEARCH_TERMS_ANCHOR = 'google-ads-search-terms';

/**
 * One toggleable cell. ⚠️ The SEARCH TERM itself is not here and is not
 * toggleable — it is the row's identity, and a table of anonymous numbers would
 * be unreadable.
 */
function cell(
  row: GoogleAdsSearchTermRow,
  key: SearchTermColumnKey,
  money?: GoogleMoneyDisplay,
): React.ReactNode {
  // ⚠️ A search-term row carries a PLAIN amount plus its own currency, so it
  // converts one amount at a time. No rate → the native figure under its native
  // marker, so the row still reads truthfully (see convertRowAmount).
  const asMoney = (amount: number): React.ReactNode => {
    const c = convertRowAmount(amount, row.currency, money?.display ?? '', money?.snapshot);
    return formatReportingAmount(c.amount ?? 0, c.currency);
  };
  switch (key) {
    case 'matchType':
      return row.matchType ? row.matchType.toLowerCase() : NOT_MEASURED;
    case 'cost':
      return asMoney(row.cost);
    case 'impressions':
      return formatCount(row.impressions);
    case 'clicks':
      return formatCount(row.clicks);
    case 'ctr':
      return formatPct(row.ctr);
    case 'conversions':
      return formatDecimal(row.conversions);
    case 'costPerConversion':
      // No conversions → no cost-per-conversion. "—", never a divide-by-zero
      // infinity or a fabricated 0.
      return row.costPerConversion === null ? NOT_MEASURED : asMoney(row.costPerConversion);
  }
}

export function SearchTermsPanel({
  data,
  loading,
  columns,
  onColumnsChange,
  money,
}: {
  data: GoogleAdsSearchTermsResponse | undefined;
  loading: boolean;
  columns: SearchTermColumnKey[];
  onColumnsChange: (next: SearchTermColumnKey[]) => void;
  /** The agency display currency. Absent = native. */
  money?: GoogleMoneyDisplay;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('cost');

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const q = query.trim().toLowerCase();
    const filtered = q ? all.filter((r) => r.searchTerm.toLowerCase().includes(q)) : all;
    return [...filtered].sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));
  }, [data?.rows, query, sortKey]);

  const scopeNote =
    data?.scopeLevel === 'adGroup'
      ? 'Scoped to the focused ad group — Google reports search terms by ad group, never per ad.'
      : data?.scopeLevel === 'campaign'
        ? 'Scoped to the focused campaign.'
        : // The top scope is the LOCATION (or all of them), which is the union of
          // its campaigns' terms — the only way a shared Google account's terms
          // split per location at all.
          'Across every location in the current filters.';

  return (
    <section
      id={SEARCH_TERMS_ANCHOR}
      className="flex min-h-0 scroll-mt-4 flex-col rounded-lg border border-border bg-card"
    >
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold">Search terms</h2>
          <p className="text-xs text-muted-foreground">
            {scopeNote}
            {data && data.capped && (
              <>
                {' '}Showing the top {data.rows.length} of {formatCount(data.totalTerms)} by cost.
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter terms…"
              aria-label="Filter search terms"
              className="h-8 w-48 rounded-md border border-input bg-background pl-7 pr-2 text-sm"
            />
          </div>
          {/* ⚠️ The sort options are the full fixed list even when a column is
              hidden: the data is still there, and sorting by something you
              aren't displaying is a legitimate thing to want. */}
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            aria-label="Sort search terms"
            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="cost">Sort: Cost</option>
            <option value="clicks">Sort: Clicks</option>
            <option value="impressions">Sort: Impressions</option>
            <option value="conversions">Sort: Conversions</option>
            <option value="ctr">Sort: CTR</option>
          </select>
          <ColumnsMenu
            options={SEARCH_TERM_COLUMNS}
            selected={columns}
            onChange={(next) => onColumnsChange(next as SearchTermColumnKey[])}
            // Says the limit out loud rather than leaving an AM hunting for a
            // metric this endpoint simply doesn't return.
            title="Choose the columns shown for each search term (these are the fields Google reports here)"
          />
        </div>
      </header>

      {loading && !data ? (
        <div className="space-y-2 p-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-6 w-full animate-pulse rounded bg-muted" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">
          {query ? 'No terms match that filter.' : 'No search terms in this scope.'}
        </p>
      ) : (
        // ⚠️ THE max-h IS A FLOOR, NOT THE HEIGHT. Standalone, this panel is
        // 28rem tall. INSIDE a grid panel the height is the user's — dragged to
        // whatever they resized the box to — so `reporting-table-panel-scroll`
        // (scoped under .reporting-chart-panel-body) makes this region grow and
        // sets `max-height: none`, or the panel would gain empty space below an
        // unchanged 28rem table. Same class of gotcha as the charts' fill CSS,
        // without needing the remount nonce: a table reflows on its own.
        <div className="reporting-table-panel-scroll max-h-[28rem] min-h-0 overflow-auto scroll-subtle">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="text-left text-xs text-muted-foreground shadow-[inset_0_-1px_0_hsl(var(--border))]">
                {/* Always present — the row's identity, never toggleable. */}
                <th className="px-4 py-2 font-medium">Search term</th>
                {columns.map((key) => (
                  <th
                    key={key}
                    className={`px-3 py-2 font-medium ${key === 'matchType' ? '' : 'text-right'}`}
                  >
                    {SEARCH_TERM_COLUMNS.find((c) => c.key === key)?.label ?? key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={`${r.searchTerm}|${r.matchType}|${r.currency}`}
                  className="border-b border-border last:border-0"
                >
                  <td className="max-w-[24rem] truncate px-4 py-2" title={r.searchTerm}>
                    {r.searchTerm}
                  </td>
                  {columns.map((key) => (
                    <td
                      key={key}
                      className={
                        key === 'matchType'
                          ? 'px-3 py-2 text-xs text-muted-foreground'
                          : 'px-3 py-2 text-right tabular-nums'
                      }
                    >
                      {cell(r, key, money)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
